/**
 * Whether payment initiation is currently allowed, according to the backend.
 *
 * WHY THIS IS A FETCH AND NOT AN ENV FLAG
 * ---------------------------------------
 * The obvious implementation is a build-time `import.meta.env` flag. It would
 * be shorter, synchronous, and completely useless: such a value is baked into
 * the bundle, so it cannot change when an operator flips a Railway variable
 * during an incident. The Upgrade button would stay live through exactly the
 * event the kill switch was thrown for. The backend reads the environment
 * variable per request and reports it on `/api/v1/payments/config`
 * (unauthenticated, already the endpoint the frontend reads), so that is the
 * only source of truth. A structural test fails the build if anyone
 * reintroduces a build-time payments flag; it matches on the identifier, so
 * this comment deliberately does not spell one out.
 *
 * FAIL CLOSED
 * -----------
 * Reversed by Ash's decision after the round-1 audit; an earlier version of
 * this file failed open and argued for it. Anything other than a clear
 * `payments_enabled: true` from the backend means payments are not offered:
 *
 *   - request rejected, or aborted by our own timeout
 *   - non-OK status
 *   - body that is not JSON
 *   - field absent (an older backend) or not a boolean
 *   - still in flight: nothing is known yet, so nothing is offered
 *
 * The cost is real and was accepted: a transient `/config` failure hides the
 * Upgrade button, so a network blip becomes a brief self-inflicted sales
 * outage. The thing bought is that no buyable button is ever shown during an
 * incident, and no user reaches a hard 503 they cannot interpret.
 *
 * `unavailable` separates "we could not find out" from "the backend told us
 * payments are off". Both hide the button; only the first is a fault on our
 * side, and only it should read as a temporary glitch to the user.
 *
 * This is still not the enforcement point - `/create-checkout` returns 503
 * regardless of what the UI believes. It is now a closed door rather than an
 * open one, which is a UX decision, not a security boundary.
 */

import { useEffect, useState } from 'react';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  'https://claude-essay-agent-production.up.railway.app';

/**
 * Long enough that a slow mobile connection is not called a failure, short
 * enough that nobody stares at a disabled button wondering. Without any
 * timeout a hung request would leave the UI in `loading` forever: still
 * fail-closed, but with no message explaining why.
 */
const CONFIG_TIMEOUT_MS = 8000;

/**
 * Did this failed checkout response fail because the kill switch is on?
 *
 * Needed because the call sites could not previously tell the switch apart
 * from any other failure. `pages/PricingPage.tsx` sent a logged-in user to
 * /signup on ANY non-OK response, so throwing the switch bounced paying
 * customers to a signup page.
 *
 * Reads the reason, not the status code: 503 is also returned when Stripe keys
 * are missing and when the webhook idempotency store is unavailable.
 */
export async function wasBlockedByKillSwitch(
  response: Response,
): Promise<boolean> {
  if (response.status !== 503) return false;
  try {
    const body = await response.clone().json();
    return body?.detail?.reason === 'payments_disabled';
  } catch {
    return false;
  }
}

export interface PaymentsEnabledState {
  /** True ONLY on an explicit `payments_enabled: true` from the backend. */
  paymentsEnabled: boolean;
  /** True until the first answer (or timeout). Payments are not offered yet. */
  loading: boolean;
  /**
   * True when the state could not be determined at all. Distinct from the
   * backend answering "off": this one is our fault and reads as temporary.
   */
  unavailable: boolean;
}

/** What to show the user. Neutral on purpose - it names no provider. */
export const PAYMENTS_UNAVAILABLE_MESSAGE =
  'Payments are temporarily unavailable. Your existing plan and credits are ' +
  'unaffected. Please try again shortly.';

export function usePaymentsEnabled(): PaymentsEnabledState {
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS);

    (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/payments/config`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          if (!cancelled) setUnavailable(true);
          return;
        }
        const data = await response.json();
        // Strict identity both ways. `Boolean("false")` is true, and a missing
        // field must not be read as either answer - it means this backend
        // predates the field, which we cannot interpret.
        if (data?.payments_enabled === true) {
          if (!cancelled) setPaymentsEnabled(true);
        } else if (data?.payments_enabled === false) {
          // A real answer: a deliberate pause, not a fault of ours.
          if (!cancelled) setPaymentsEnabled(false);
        } else if (!cancelled) {
          setUnavailable(true);
        }
      } catch {
        // Rejected, or aborted by our timeout. Either way: unknown, so closed.
        if (!cancelled) setUnavailable(true);
      } finally {
        clearTimeout(timer);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, []);

  return { paymentsEnabled, loading, unavailable };
}
